import { coreSync } from './core.js';
import { documentFromRows } from './documents.js';
import { isActive, tombstoneOf } from './tombstone.js';

// **Adapters.** The folds, the Save rule, the
// session document and the Practice Log's order are `core/src/practice.rs`,
// so a phone's Practice Overview adds up the same minutes; what stays is the
// coercion — a stored `minutes` read with `Number()`, the tombstone as
// `tombstone.js` reduces it. `core/src/lesson_vectors.json` holds this file's
// recorded answers.
const str = (v) => (v == null ? null : String(v));
const num = (v) => (v == null ? null : Number(v));

const sessionInput = (e) => ({
  practicedOn: str(e?.practicedOn),
  minutes: num(e?.minutes),
  ...tombstoneOf(e),
});
// Practice log.
//
// Sessions are appended, never edited into a running total — the money
// ledger's rule: a stored "minutes this week" is a mutable cell two offline
// devices would both overwrite.
//
// Minutes are integers, so the weekly fold is exact.

// Two fields, and the boolean is the one this believes — see ledger.js's
// isActive (#70). No writer retracts a practice session; the folds read both
// halves of the tombstone all the same.
// Imported as well as re-exported: a bare `export … from` creates no local
// binding, and this module uses the predicate itself.
export { isActive };

export function totalMinutes(entries) {
  return coreSync().practiceTotalMinutes(entries.map(sessionInput));
}

export function entriesBetween(entries, startISO, endISO) {
  return coreSync().practiceEntriesBetween(entries.map(sessionInput), str(startISO), str(endISO))
    .map((i) => entries[i]);
}

// Distinct days practised, not sessions — three sessions on Tuesday is one day
// of practice.
export function daysPractised(entries) {
  return coreSync().practiceDaysPractised(entries.map(sessionInput));
}

export function formatDuration(minutes) {
  return coreSync().practiceFormatDuration(num(minutes));
}

// The three figures on the profile's Practice Overview, each for the given
// window. `asOf` is a YYYY-MM-DD business date so the summary is stable
// regardless of when it is rendered or in which timezone.
// Averaged across the whole window, not just days practised: four 30-minute
// sessions in a month read "0:12 a day".
export function practiceSummary(entries, asOf) {
  const s = coreSync().practiceSummary(entries.map(sessionInput), asOf);
  return {
    daysThisWeek: s.daysThisWeek,
    daysLast30: s.daysLast30,
    minutesThisWeek: s.minutesThisWeek,
    minutesLast30: s.minutesLast30,
    averageMinutesPerDay: s.averageMinutesPerDay,
  };
}

// ── Logging a session ───────────────────────────────────────────────────────
//
// Shared by `addSession` in src/data/usePractice.jsx, the Add Practice Time
// drawer and the phones, so a session is logged with the same document and
// the same Save rule.

// Save is offered once Date holds a date and Minutes a positive whole number
// the rules store (PracticeTimeDrawer.jsx), so a cleared date or `-5` minutes is
// not saved.
export function practiceCanSave(minutes, practicedOn) {
  return coreSync().practiceSessionCanSave(str(practicedOn), num(minutes));
}

// The session a Save appends. Whole minutes, never below zero; both halves
// of a live tombstone written with it.
export function practiceSessionDocument({ studentId, practicedOn, minutes, note }, serverTimestamp) {
  return documentFromRows(coreSync().practiceSessionDocument(
    String(studentId), str(practicedOn), num(minutes), str(note),
  ), serverTimestamp);
}

// A student's sessions as the Practice Log lists them, newest first, two on
// one day by id, descending (`newest_first_rows` in core/src/lesson.rs; a
// session has no start time) — the same order whatever order the snapshot
// delivered them in.
export function sortPracticeSessions(sessions = []) {
  return coreSync().newestFirstRows(sessions.map((s) => ({
    date: String(s.practicedOn ?? ''), startTime: null, id: String(s.id ?? ''),
  }))).map((i) => sessions[i]);
}
